/**
 * Account keys, derived from the password in this browser. The password
 * never leaves it:
 *
 * - Argon2id stretches the password (with the account's salt and
 *   parameters) into a master key, deliberately slowly.
 * - The master key yields two keys. The auth key is sent at sign-in; the
 *   Hub stores only its hash. The vault key never leaves the browser.
 * - The vault key encrypts the user's X25519 private key, which opens the
 *   client organizations' keys shared with them.
 *
 * So the Hub can't sign in as a user or read their vault, and one password
 * both signs in and unlocks every client a person is assigned to.
 */
import { x25519 } from '@noble/curves/ed25519';
import { argon2idAsync } from '@noble/hashes/argon2';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { base64ToBytes, bytesToBase64, concatBytes } from '~/util/bytes';
/** Long enough that guessing it through Argon2id is out of reach. */
export const MIN_PASSWORD_LENGTH = 12;
/** Mirrors `KdfParams::recommended` on the Hub: 64 MiB, three passes. */
export function newKdfParams(salt = randomBytes(16)) {
    return {
        algorithm: 'argon2id',
        salt: bytesToBase64(salt),
        memory_kib: 64 * 1024,
        iterations: 3,
        parallelism: 1,
    };
}
/** What's wrong with a new password, or null when it's fine. */
export function passwordProblem(password, confirmation) {
    if (password.length < MIN_PASSWORD_LENGTH) {
        return `Use at least ${MIN_PASSWORD_LENGTH} characters. A short sentence works well.`;
    }
    if (password !== confirmation)
        return "The two passwords don't match.";
    return null;
}
/** The same bounds the Hub enforces when credentials are saved. */
const KDF_LIMITS = {
    memoryKib: [19 * 1024, 1024 * 1024],
    iterations: [2, 10],
    parallelism: [1, 4],
    saltBytes: [16, 64],
};
const within = (value, [low, high]) => Number.isInteger(value) && value >= low && value <= high;
/** What's wrong with password settings, or null when they're acceptable. */
export function kdfProblem(kdf) {
    let saltBytes = 0;
    try {
        saltBytes = base64ToBytes(kdf.salt).length;
    }
    catch {
        // Reported below.
    }
    const acceptable = kdf.algorithm === 'argon2id' &&
        within(kdf.memory_kib, KDF_LIMITS.memoryKib) &&
        within(kdf.iterations, KDF_LIMITS.iterations) &&
        within(kdf.parallelism, KDF_LIMITS.parallelism) &&
        within(saltBytes, KDF_LIMITS.saltBytes);
    return acceptable
        ? null
        : 'The Hub asked for password settings this page refuses, so nothing was sent. Contact your firm admin.';
}
const AUTH_INFO = 'zunox/account-auth/v1';
const VAULT_INFO = 'zunox/account-vault/v1';
/**
 * Derives keys with settings the Hub supplied (before signing in, or for a
 * password check). They're checked first: a compromised Hub (or anything
 * between it and this page) could otherwise ask for weak settings, making
 * the auth key it receives cheap to guess the password from, and the vault
 * with it. A huge memory setting would also freeze the tab.
 */
export async function deriveAccountKeysFromHub(password, kdf, onProgress) {
    const problem = kdfProblem(kdf);
    if (problem)
        throw new VaultError(problem);
    return deriveAccountKeys(password, kdf, onProgress);
}
/** Stretches the password and derives the auth and vault keys. Takes a
 * second or two by design; `onProgress` reports 0 to 1. Use
 * `deriveAccountKeysFromHub` for settings that came from the Hub. */
export async function deriveAccountKeys(password, kdf, onProgress) {
    if (kdf.algorithm !== 'argon2id')
        throw new Error('Unsupported password settings.');
    const master = await argon2idAsync(new TextEncoder().encode(password.normalize('NFKC')), base64ToBytes(kdf.salt), {
        t: kdf.iterations,
        m: kdf.memory_kib,
        p: kdf.parallelism,
        dkLen: 32,
        asyncTick: 10,
        onProgress,
    });
    const keys = {
        authKey: hkdf(sha256, master, undefined, AUTH_INFO, 32),
        vaultKey: hkdf(sha256, master, undefined, VAULT_INFO, 32),
    };
    master.fill(0);
    return keys;
}
const WRAP_VERSION = 1;
const WRAP_AAD = new TextEncoder().encode('zunox/vault-private-key/v1');
const NONCE_BYTES = 12;
async function aesKey(raw, usage) {
    return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, [usage]);
}
/** The private key encrypted under the vault key: a version byte, a nonce
 * and the AES-GCM ciphertext. */
export async function wrapPrivateKey(vaultKey, privateKey) {
    const nonce = randomBytes(NONCE_BYTES);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: WRAP_AAD }, await aesKey(vaultKey, 'encrypt'), privateKey);
    return bytesToBase64(concatBytes(Uint8Array.of(WRAP_VERSION), nonce, new Uint8Array(ciphertext)));
}
export class VaultError extends Error {
}
/** Opens the private key. Fails if the password (so the vault key) is wrong. */
export async function unwrapPrivateKey(vaultKey, wrapped) {
    const bytes = base64ToBytes(wrapped);
    if (bytes[0] !== WRAP_VERSION || bytes.length <= 1 + NONCE_BYTES) {
        throw new VaultError('This account key is in a format this page does not know.');
    }
    try {
        const plaintext = await crypto.subtle.decrypt({
            name: 'AES-GCM',
            iv: bytes.subarray(1, 1 + NONCE_BYTES),
            additionalData: WRAP_AAD,
        }, await aesKey(vaultKey, 'decrypt'), bytes.subarray(1 + NONCE_BYTES));
        return new Uint8Array(plaintext);
    }
    catch {
        throw new VaultError("That password doesn't open this account's keys.");
    }
}
/** Credentials for a new password. */
export async function credentialsFor(password, options = {}) {
    const privateKey = options.privateKey ?? x25519.utils.randomPrivateKey();
    const kdf = options.kdf ?? newKdfParams();
    const { authKey, vaultKey } = await deriveAccountKeys(password, kdf, options.onProgress);
    return {
        privateKey,
        credentials: {
            kdf,
            auth_key: bytesToBase64(authKey),
            public_key: bytesToBase64(x25519.getPublicKey(privateKey)),
            wrapped_private_key: await wrapPrivateKey(vaultKey, privateKey),
        },
    };
}
function randomBytes(count) {
    return crypto.getRandomValues(new Uint8Array(count));
}
